<script setup lang="ts">
import type {
  MeResponse,
  UpdateProfileRequest,
} from '@/js/account/shared/model';
import { computed, reactive, ref, watch } from 'vue';
import BaseButton from '@/js/account/components/base/BaseButton.vue';
import BaseField from '@/js/account/components/base/BaseField.vue';
import BaseIcon from '@/js/account/components/base/BaseIcon.vue';
import BaseTitle from '@/js/account/components/base/BaseTitle.vue';
import { accountMeUpdate } from '@/js/account/entities/user';
import { useFormErrors } from '@/js/account/shared/lib';
import { classNames } from '@/js/lib/bem';

const props = defineProps<{
  user: MeResponse
}>();

const emit = defineEmits<{
  saved: [{ emailChanged: boolean }]
}>();

const cn = classNames('profile-details-card');

const editing = ref(false);
const saving = ref(false);
const showPassword = ref(false);

const form = reactive({
  name: '',
  email: '',
  phone: '',
  whatsapp: '',
  password: '',
});

const {
  clearErrors,
  clearFieldOnInput,
  setFromAxiosError,
  fieldError,
  hasFieldError,
  generalError,
} = useFormErrors();

const passwordInputType = computed(() =>
  showPassword.value ? 'text' : 'password',
);

const viewFields = computed(() => [
  { label: 'Full name', value: props.user.name },
  { label: 'Email', value: props.user.email },
  { label: 'Phone', value: props.user.phone ?? '—' },
  { label: 'WhatsApp', value: props.user.whatsapp ?? '—' },
  { label: 'Password', value: '************', masked: true },
]);

function resetFormFromUser(): void {
  form.name = props.user.name;
  form.email = props.user.email;
  form.phone = props.user.phone ?? '';
  form.whatsapp = props.user.whatsapp ?? '';
  form.password = '';
  showPassword.value = false;
}

function startEditing(): void {
  clearErrors();
  resetFormFromUser();
  editing.value = true;
}

function buildPayload(): UpdateProfileRequest {
  const payload: UpdateProfileRequest = {
    name: form.name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim() || null,
    whatsapp: form.whatsapp.trim() || null,
  };

  const password = form.password.trim();

  if (password) {
    payload.password = password;
  }

  return payload;
}

async function onSave(): Promise<void> {
  clearErrors();
  saving.value = true;

  try {
    // The backend lowercases the address, so a case-only edit is not a change.
    const emailChanged
      = form.email.trim().toLowerCase() !== props.user.email.toLowerCase();

    await accountMeUpdate(buildPayload());
    editing.value = false;
    emit('saved', { emailChanged });
  }
  catch (error) {
    setFromAxiosError(error);
  }
  finally {
    saving.value = false;
  }
}

watch(
  () => props.user,
  () => {
    if (!editing.value) {
      resetFormFromUser();
    }
  },
);
</script>

<template>
  <section :class="cn()">
    <header :class="cn('header')">
      <BaseTitle size="sm">
        My Profile
      </BaseTitle>

      <BaseButton
        v-if="!editing"
        theme="primary"
        size="sm"
        :class="cn('action-button')"
        @click="startEditing"
      >
        <BaseIcon name="edit" :width="24" :height="24" aria-hidden="true" />
        Edit
      </BaseButton>

      <BaseButton
        v-else
        type="submit"
        form="profile-details-form"
        theme="primary"
        size="sm"
        :class="cn('action-button')"
        :loading="saving"
      >
        <BaseIcon name="save" :width="24" :height="24" aria-hidden="true" />
        Save
      </BaseButton>
    </header>

    <div v-if="!editing" :class="cn('fields')">
      <div v-for="field in viewFields" :key="field.label" :class="cn('field')">
        <span :class="cn('field-label')">{{ field.label }}</span>
        <span :class="cn('field-value', { masked: field.masked || undefined })">
          {{ field.value }}
        </span>
      </div>
    </div>

    <form
      v-else
      id="profile-details-form"
      :class="cn('form')"
      @submit.prevent="onSave"
    >
      <BaseField
        v-model="form.name"
        :class="cn('form-field')"
        label="Full name"
        name="name"
        autocomplete="name"
        :invalid="hasFieldError('name')"
        :message="fieldError('name')"
        required
        @update:model-value="clearFieldOnInput('name')"
      />
      <BaseField
        v-model="form.email"
        :class="cn('form-field')"
        label="Email"
        name="email"
        type="email"
        autocomplete="email"
        :invalid="hasFieldError('email')"
        :message="fieldError('email')"
        required
        @update:model-value="clearFieldOnInput('email')"
      />
      <BaseField
        v-model="form.phone"
        :class="cn('form-field')"
        label="Phone"
        name="phone"
        type="tel"
        autocomplete="tel"
        :invalid="hasFieldError('phone')"
        :message="fieldError('phone')"
        @update:model-value="clearFieldOnInput('phone')"
      />
      <BaseField
        v-model="form.whatsapp"
        :class="cn('form-field')"
        label="WhatsApp"
        name="whatsapp"
        type="tel"
        autocomplete="tel"
        :invalid="hasFieldError('whatsapp')"
        :message="fieldError('whatsapp')"
        @update:model-value="clearFieldOnInput('whatsapp')"
      />

      <div :class="cn('password-field')">
        <BaseField
          v-model="form.password"
          :class="cn('form-field', { nested: true })"
          label="Password"
          name="password"
          :type="passwordInputType"
          autocomplete="new-password"
          :invalid="hasFieldError('password')"
          :message="fieldError('password')"
          @update:model-value="clearFieldOnInput('password')"
        />
        <button
          type="button"
          :class="cn('password-toggle')"
          :aria-label="showPassword ? 'Hide password' : 'Show password'"
          :aria-pressed="showPassword"
          @click="showPassword = !showPassword"
        >
          <BaseIcon name="eye" :width="20" :height="20" />
        </button>
      </div>

      <p v-if="generalError" :class="cn('form-error')" role="alert">
        {{ generalError }}
      </p>
    </form>
  </section>
</template>

<style lang="scss" scoped>
.profile-details-card {
  padding: 24px;
  border-radius: 4px;
  background-color: var(--color-white);
  box-shadow: var(--shadow);
  max-width: 400px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 40px;
  }

  &__action-button {
    gap: 8px;
    min-height: 36px;
    padding: 8px 16px 8px 8px;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__field-label {
    color: var(--color-accent);
    font-family: var(--font-family-sans);
    font-size: 14px;
    line-height: 1.3;
  }

  &__field-value {
    color: var(--color-text-primary);
    font-family: var(--font-family-sans);
    font-size: 16px;
    line-height: 1.3;
    word-break: break-word;

    &_masked {
      letter-spacing: 0.08em;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
  }

  &__form-field {
    margin-bottom: 24px;

    &_nested {
      margin-bottom: 0;
    }
  }

  &__password-field {
    position: relative;

    :deep(input) {
      padding-right: 48px;
    }
  }

  &__password-toggle {
    position: absolute;
    top: 50%;
    right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: none;
    color: var(--color-accent);
    cursor: pointer;
    transform: translateY(-50%);

    &:hover {
      color: var(--color-primary);
    }
  }

  &__form-error {
    margin: 0;
    color: var(--base-field-error-color, #db1515);
    font-family: var(--font-family-sans);
    font-size: 12px;
    line-height: 1.3;
  }

  @media (max-width: 768px) {
    padding: 24px 16px;

    &__header {
      margin-bottom: 24px;
    }
  }
}
</style>
